import type { CartesianPose } from '@sb/geometry/CartesianPose';
import {
  interpolateCartesianPoses,
  posesAreEqual,
} from '@sb/geometry/CartesianPose';
import { cartesianDistance } from '@sb/geometry/CartesianPosition';
import { makeNamespacedLog } from '@sb/log';
import type {
  ArmTarget,
  PoseArmTarget,
  TCPOffsetOption,
} from '@sb/motion-planning';
import type {
  EquipmentStateItem,
  RoutineContext,
  StepFailure,
} from '@sb/routine-runner';
import { FailureKind } from '@sb/routine-runner';
import type {
  StepPlayArguments,
  StepAction,
} from '@sb/routine-runner/Step/Step';
import Step from '@sb/routine-runner/Step/Step';
import type WaypointStep from '@sb/routine-runner/Step/Waypoint/Step';
import { wait } from '@sb/utilities';
import type { NonEmptyArray } from '@sb/utilities/src/types';

import { WeldArguments } from './Arguments';
import { WeldVariables } from './Variables';

const log = makeNamespacedLog('WeldStep');

export class WeldStep extends Step<WeldArguments, WeldVariables> {
  public static areSubstepsRequired = true;

  public static Arguments = WeldArguments;

  public static Variables = WeldVariables;

  public substeps: Array<Step<object, object>> = [];

  protected initializeVariableState(): void {
    const { completedWeldCount = 0 } = this.variablesForInitialization;

    this.variables = {
      completedWeldCount,
      currentActivity: 'none',
      approachMovementActivity: 'none',
      weldMovementActivity: 'none',
    };
  }

  private motionApproach?: ReturnType<RoutineContext['doMotion']>;

  private motionWeld?: ReturnType<RoutineContext['doMotion']>;

  public async _play({
    fail,
    hasEncounteredStartStep,
  }: StepPlayArguments): Promise<void> {
    if (!hasEncounteredStartStep()) {
      // Skip weld steps if we haven't reached the start step
      log.info(
        '_play',
        "Skipping Weld step because we haven't reached the start step yet",
      );

      return;
    }

    const { weldMachine, torch } = this.activeEquipment;

    if (weldMachine == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld machine not found',
      });

      return;
    }

    if (torch == null) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'Weld torch not found',
      });

      return;
    }

    this.setRoutineContextTCPOffsetOption();

    if (this.substeps.length === 0) {
      fail({
        failure: {
          kind: FailureKind.WeldFailure,
        },
        failureReason: 'No waypoints found',
      });

      return;
    }

    // Get the approach target and return early if it fails
    const [approachTarget] = (await this.getArmTargets({
      substeps: [this.substeps[0]] as WaypointStep[],
      fail,
      // Don't convert to pose arm target for approach target because user may have set shouldMatchJointAngles to true
      convertToPose: false,
    })) ?? [undefined];

    if (approachTarget === undefined) {
      return;
    }

    this.setVariable('currentActivity', 'approaching');

    const approachCompleted = await this.moveToPosition({
      fail,
      armTarget: approachTarget,
    });

    if (!approachCompleted) {
      log.warn('_play', 'Approach movement failed. Returning.', {
        variables: this.variables,
      });

      return;
    }

    this.setVariable('currentActivity', 'settingWeldParameters');
    await this.setWeldMachineParameters({ weldMachine, fail });

    if (!this.args.stitching.enabled) {
      await this.performContinuousWeld({ weldMachine, fail });
    } else {
      await this.performStitchedWeld({ weldMachine, fail });
    }

    this.setVariable('currentActivity', 'none');

    this.setVariable(
      'completedWeldCount',
      this.variables.completedWeldCount + 1,
    );
  }

  /**
   * Perform a continuous weld without stitching
   */
  private async performContinuousWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    const waypoints = this.substeps as WaypointStep[];

    const targets = await this.getArmTargets({
      substeps: waypoints.slice(1) as WaypointStep[],
      fail,
      convertToPose: true,
    });

    if (targets === undefined) {
      log.error('performContinuousWeld', 'No targets found. Returning.');

      return;
    }

    const weldCompleted = await this.moveDuringWeld({
      targets,
      fail,
      startWelding: async () => {
        this.setVariable('currentActivity', 'startArc');
        await this.startWeldMachine({ weldMachine, fail });
        await wait(this.args.arcStartTime);
      },
    });

    // If weld doesn't complete, stop arc right away. No crater filling.
    if (weldCompleted) {
      this.setVariable('currentActivity', 'craterFilling');

      await wait(this.args.craterFillTime);
    }

    this.setVariable('currentActivity', 'stopArc');
    await this.stopWeldMachine({ weldMachine, fail });
  }

  /**
   * Perform a stitched weld, alternating between welding and not welding
   */
  private async performStitchedWeld({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    const waypoints = this.substeps as WaypointStep[];

    const targets = await this.getArmTargets({
      substeps: waypoints as WaypointStep[],
      fail,
      convertToPose: true,
    });

    if (targets === undefined) {
      log.error('performStitchedWeld', 'No targets found. Returning.');

      return;
    }

    if (targets.length < 2) {
      log.warn('_play', 'Need at least two waypoints for stitch welds');

      return;
    }

    // Process each waypoint segment (from point A to point B)
    for (let i = 0; i < targets.length - 1; i += 1) {
      const startTarget = targets[i];
      const endTarget = targets[i + 1];

      log.debug(
        'performStitchedWeld',
        `Processing waypoint segment ${i} to ${i + 1}`,
      );

      // Split this segment into stitch and space sub-segments
      const subTargets = this.generateSegments(startTarget, endTarget);

      // Process each sub-segment
      for (let j = 0; j < subTargets.length; j += 1) {
        const isWeldTarget = j % 2 === 0; // even segments are weld segments
        const target = subTargets[j];

        log.debug(
          'performStitchedWeld',
          `  Processing sub-segment ${j}, weld=${isWeldTarget}`,
        );

        let movementCompleted: boolean;

        if (isWeldTarget) {
          movementCompleted = await this.moveDuringWeld({
            targets: [target],
            fail,
            startWelding: async () => {
              this.setVariable('currentActivity', 'startArc');
              await this.startWeldMachine({ weldMachine, fail });
              await wait(this.args.arcStartTime);
            },
          });
        } else {
          this.setVariable('currentActivity', 'movingToNextStitch');

          movementCompleted = await this.moveToPosition({
            fail,
            armTarget: target,
          });
        }

        if (!movementCompleted) {
          // Stop welding if movement failed
          this.setVariable('currentActivity', 'stopArc');
          await this.stopWeldMachine({ weldMachine, fail });

          return;
        }

        if (isWeldTarget) {
          // Stop welding after this segment
          if (
            this.args.stitching.craterFillAfterEveryStitch ||
            // Last subtarget of last waypoint
            (j >= subTargets.length - 2 && i === waypoints.length - 2)
          ) {
            this.setVariable('currentActivity', 'craterFilling');
            await wait(this.args.craterFillTime);
          }

          this.setVariable('currentActivity', 'stopArc');
          await this.stopWeldMachine({ weldMachine, fail });
        }
      }
    }
  }

  /**
   * Generate stitch and space segments for a weld path
   */
  private generateSegments(
    startTarget: ArmTarget & PoseArmTarget,
    endTarget: ArmTarget & PoseArmTarget,
  ): ArmTarget[] {
    const stitchLength = this.args.stitching.stitch;
    const spaceLength = this.args.stitching.space;
    const totalDistance = cartesianDistance(startTarget.pose, endTarget.pose);

    const targets: ArmTarget[] = [];
    let currentDistance = 0;
    const MINIMUM_STITCH_DISTANCE = 0.005; // 5 mm, adjust as needed

    // initialize spaceEnd to startTarget
    let spaceEnd = startTarget;

    while (currentDistance < totalDistance - MINIMUM_STITCH_DISTANCE) {
      const interpolatedResult = this.interpolate(
        startTarget,
        endTarget,
        Math.min((currentDistance + stitchLength) / totalDistance, 1.0),
      );

      const stitchEnd = {
        ...interpolatedResult,
        pose: {
          x: interpolatedResult.pose.x,
          y: interpolatedResult.pose.y,
          z: interpolatedResult.pose.z,
          // use previous spaceEnd orientation for stitch segments (this maintains fixed orientation for the weld)
          i: spaceEnd.pose.i,
          j: spaceEnd.pose.j,
          k: spaceEnd.pose.k,
          w: spaceEnd.pose.w,
        },
        motionKind: 'line' as const, // hardcoded for now
      };

      targets.push(stitchEnd);

      log.info('generateSegments', 'Stitch segment', {
        start: {
          x: spaceEnd.pose.x,
          y: spaceEnd.pose.y,
          z: spaceEnd.pose.z,
        },
        end: {
          x: stitchEnd.pose.x,
          y: stitchEnd.pose.y,
          z: stitchEnd.pose.z,
        },
      });

      currentDistance += stitchLength;

      // If we've reached the end, we're done
      if (currentDistance >= totalDistance) {
        // if our final orientation is different from the end target, add a space segment to reach the end target
        if (!posesAreEqual(stitchEnd.pose, endTarget.pose)) {
          targets.push(endTarget);
        }

        break;
      }

      // Create space segment
      spaceEnd = {
        ...this.interpolate(
          startTarget,
          endTarget,
          Math.min((currentDistance + spaceLength) / totalDistance, 1.0),
        ),
        motionKind: 'line' as const, // hardcoded for now
      };

      targets.push(spaceEnd);

      log.info('generateSegments', 'Space segment', {
        start: {
          x: stitchEnd.pose.x,
          y: stitchEnd.pose.y,
          z: stitchEnd.pose.z,
        },
        end: {
          x: spaceEnd.pose.x,
          y: spaceEnd.pose.y,
          z: spaceEnd.pose.z,
        },
      });

      currentDistance += spaceLength;
    }

    if (currentDistance < totalDistance) {
      // replace last space target with end target
      // this happens when there isn't enough room for another stitch
      targets.pop();
      targets.push(endTarget);
    }

    return targets;
  }

  /**
   * Interpolate between two arm targets by a given fraction
   */
  private interpolate(
    start: ArmTarget & PoseArmTarget,
    end: ArmTarget & PoseArmTarget,
    fraction: number,
  ): ArmTarget & PoseArmTarget {
    return {
      ...start,
      pose: interpolateCartesianPoses(start.pose, end.pose, fraction),
    };
  }

  public async _stop({ fail }: StepAction): Promise<void> {
    log.info('_stop', 'Stopping Weld step');

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_stop', 'No weld machine found. Nothing to stop.');

      return;
    }

    this.setVariable('currentActivity', 'stopArc');
    await this.stopWeldMachine({ weldMachine, fail });

    if (this.motionApproach != null) {
      this.motionApproach.emit('cancel');
    }

    if (this.motionWeld != null) {
      this.motionWeld.emit('cancel');
    }

    this.setVariable('currentActivity', 'none');
  }

  public async _pause({ fail }: StepAction): Promise<void> {
    log.info('_pause', 'Pausing Weld step');

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_pause', 'No weld machine found. Nothing to pause.');

      return;
    }

    await this.stopWeldMachine({ weldMachine, fail });

    if (this.motionApproach != null) {
      this.motionApproach.emit('pause');
    }

    if (this.motionWeld != null) {
      this.motionWeld.emit('pause');
    }
  }

  public async _resume({ fail }: StepAction): Promise<void> {
    log.info('_resume', 'Resuming Weld step');

    const { weldMachine } = this.activeEquipment;

    if (weldMachine == null) {
      log.info('_resume', 'No weld machine found. Nothing to resume.');

      return;
    }

    // Main question: do we need to be starting weld machine again once approach is complete?
    if (this.motionApproach != null) {
      this.motionApproach.emit('resume');
      this.setVariable('approachMovementActivity', 'moving');
    }

    if (this.motionWeld != null) {
      this.setVariable('currentActivity', 'startArc');

      await this.startWeldMachine({ weldMachine, fail });

      this.motionWeld.emit('resume');
      this.setVariable('weldMovementActivity', 'moving');
      this.setVariable('currentActivity', 'movingDuringWeld');
    }
  }

  private get activeEquipment(): {
    weldMachine: EquipmentStateItem | undefined;
    torch: EquipmentStateItem | undefined;
  } {
    const allEquipment = this.routineContext.equipment.getEquipmentState();

    const weldMachine = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedMachineID,
    );

    const torch = allEquipment.find(
      (equipment) => equipment.id === this.args.selectedTorchID,
    );

    return { weldMachine, torch };
  }

  private getTCPOffsetOption(): TCPOffsetOption {
    const { torch } = this.activeEquipment;

    if (torch == null) {
      throw new Error('Torch not found');
    }

    if (this.args.selectedTorchID == null) {
      throw new Error('Selected torch ID is null');
    }

    // TCP Offset option a bit of a hack. May need to revisit the indexing once we add dedicated torches, not just custom grippers.
    // See https://github.com/standardbots/sb/blob/540d718e5d2bbe3226ea9b4128b9f49392bfc6ef/libs/integrations/implementations/CustomGripper/frontend.tsx#L30
    return `ee-${torch.state.kind}-0`;
  }

  private setRoutineContextTCPOffsetOption(): void {
    const tcpOffset = this.getTCPOffsetOption();
    this.routineContext.setTCPOffsetOption(tcpOffset);
  }

  private async moveToPosition({
    fail,
    armTarget,
  }: {
    fail: (failure: StepFailure) => void;
    armTarget: ArmTarget;
  }): Promise<boolean> {
    log.info('moveToPosition', 'Moving to position');
    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    let completed = false;

    const motion = this.routineContext.doMotion({
      request: {
        targets: [armTarget] as NonEmptyArray<ArmTarget>,
      },
      speedProfiles: [this.args.approachSpeedProfile],
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
    });

    motion.on('requestingPlan', () => {
      // Only checking previous state for first few states because we could be in a play/pause/resume flow.
      if (this.variables.approachMovementActivity === 'none') {
        this.setVariable('approachMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.approachMovementActivity === 'requestingPlan') {
        this.setVariable('approachMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('approachMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('approachMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveToApproachPosition', 'Approach motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('approachMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('approachMovementActivity', 'none');
      fail(failure);
    });

    this.motionApproach = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('approachMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionApproach;
    }

    return completed;
  }

  /**
   * Handles movement one set of targets at a time.
   */
  private async moveDuringWeld({
    targets,
    fail,
    startWelding,
  }: {
    targets: ArmTarget[];
    fail: (failure: StepFailure) => void;
    startWelding: () => Promise<void>;
  }): Promise<boolean> {
    let completed = false;

    log.info('moveDuringWeld', 'Moving during weld iteration');

    if (targets.length === 0) {
      log.debug('moveDuringWeld', 'No targets found. Returning.');

      return completed;
    }

    // Weld does not observe push mode: should not be pushing up against object.
    // Also, ros2 does not support push at this time (2025-02-19).
    const motion = this.routineContext.doMotion({
      request: {
        targets: targets as NonEmptyArray<ArmTarget>,
      },
      speedProfiles: targets.map(() => this.args.weldTravelSpeedProfile),
      pushUntilCollision: false,
      pushMode: false,
      stepID: this.id,
      waitForExecutionSignal: true,
    });

    motion.on('waitingForExecutionSignal', async () => {
      await startWelding();
      log.info('moveDuringWeld', 'Emitting execution signal');
      motion.emit('executionSignal');
      this.setVariable('currentActivity', 'movingDuringWeld');
    });

    motion.on('requestingPlan', () => {
      if (this.variables.weldMovementActivity === 'none') {
        this.setVariable('weldMovementActivity', 'requestingPlan');
      }
    });

    motion.on('planning', () => {
      if (this.variables.weldMovementActivity === 'requestingPlan') {
        this.setVariable('weldMovementActivity', 'planning');
      }
    });

    motion.on('beginMotion', () => {
      this.setVariable('weldMovementActivity', 'moving');
    });

    motion.on('pause', () => {
      this.setVariable('weldMovementActivity', 'paused');
    });

    motion.on('complete', () => {
      log.debug('moveDuringWeld', 'Welding motion complete');

      completed = true;
    });

    motion.on('cancelled', () => {
      this.setVariable('weldMovementActivity', 'none');
    });

    motion.on('failure', (failure) => {
      this.setVariable('weldMovementActivity', 'none');
      fail(failure);
    });

    this.motionWeld = motion;

    try {
      await motion.race('failure', 'complete', 'cancelled');
    } finally {
      this.setVariable('weldMovementActivity', 'none');
      motion.removeAllListeners();

      delete this.motionWeld;
    }

    return completed;
  }

  /**
   * Stop the current weld machine.
   *
   * Weld stop commands may be sent even if welding hasn't started yet.
   */
  private async stopWeldMachine({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.stopMillerWeldMachine({ fail });
        this.setVisualizerWeldArcFlashEffect(false);

        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;

      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async startWeldMachine({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        try {
          await this.startMillerWeldMachine({ fail });
        } catch (error) {
          fail({
            failure: { kind: FailureKind.WeldFailure },
            failureReason: `Failed to start weld machine: ${error.message}`,
            error,
          });
        }

        this.setVisualizerWeldArcFlashEffect(true);
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async setWeldMachineParameters({
    weldMachine,
    fail,
  }: {
    weldMachine: EquipmentStateItem;
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    let failureReason: string;

    switch (weldMachine.kind) {
      case 'MillerWeldMachine':
        await this.setMillerWeldParameters({ fail });
        break;
      case 'EsabWeldMachine':
        failureReason = 'ESAB weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      case 'WeldMachine':
        failureReason = 'Generic weld machine not supported';

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
      default:
        failureReason = `Unknown weld machine type: ${weldMachine.kind}`;

        fail({
          failure: {
            kind: FailureKind.WeldFailure,
          },
          failureReason,
        });

        break;
    }
  }

  private async stopMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'stopArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to stop weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async startMillerWeldMachine({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'startArc',
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to start weld machine: ${error.message}`,
        error,
      });
    }
  }

  private async setMillerWeldParameters({
    fail,
  }: {
    fail: (failure: StepFailure) => void;
  }): Promise<void> {
    try {
      await this.routineContext.equipment.executeCommand({
        kind: 'MillerWeldMachineCommand',
        deviceID: this.args.selectedMachineID,
        subCommand: {
          kind: 'setWeldParameters',
          weldTravelSpeed: 0, // This is disregarded by machine. Just set it like this for now.
          ...this.args.millerWeldParameters,
        },
      });
    } catch (error) {
      fail({
        failure: { kind: FailureKind.WeldFailure },
        failureReason: `Failed to set weld machine parameters: ${error.message}`,
        error,
      });
    }
  }

  /**
   * Get the arm targets for the given substeps.
   *
   * Optionally converts joint arm targets to pose arm targets.
   *
   * Fancy typing ensures the output array length matches the input array length, and enforces
   * the conversion to pose arm targets if requested.
   */
  private async getArmTargets<
    T extends Array<WaypointStep>,
    P extends boolean = true,
  >({
    substeps,
    fail,
    convertToPose = true as P,
  }: {
    substeps: T;
    fail: (failure: StepFailure) => void;
    convertToPose?: P;
  }): Promise<
    | { [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget }
    | undefined
  > {
    const targets: ArmTarget[] = [];

    const { kinematicState } = this.routineContext.getRoutineRunnerState();

    let previousTargetPose: CartesianPose =
      kinematicState.tcpOffsetOption === 'wrist'
        ? kinematicState.wristPose
        : kinematicState.tooltipPoint;

    for (let i = 0; i < substeps.length; i += 1) {
      const substep = substeps[i];

      if (substep.getStepKind() !== 'Waypoint') {
        // This is a dev error. Will need to update once we add additional flourishes.
        throw new Error(
          `Substep is not a waypoint: kind=${substep.getStepKind()}`,
        );
      }

      const maybeTarget = await (substep as WaypointStep).getArmTarget({
        effectiveTCPOption: this.getTCPOffsetOption(),
        parentCompletedCount: this.variables.completedWeldCount,
        previousTargetPose,
      });

      if ('failure' in maybeTarget) {
        fail(maybeTarget);

        return undefined;
      }

      let { target } = maybeTarget;
      const { poseForPreviousTarget } = maybeTarget;

      previousTargetPose = poseForPreviousTarget;

      // Only convert to pose if requested
      if (convertToPose && !('pose' in target)) {
        target = {
          ...target,
          // this is a joint arm target so lets convert it to a pose arm target
          pose: await this.routineContext.getTargetPose(target),
        };

        if ('jointAngles' in target) {
          // remove jointAngles from target otherwise motion planner will use them
          delete target.jointAngles;
        }
      }

      targets.push(target);
    }

    return targets as {
      [K in keyof T]: P extends true ? ArmTarget & PoseArmTarget : ArmTarget;
    };
  }

  // Enable/disable the weld flash effect on the torch in visualizer
  private setVisualizerWeldArcFlashEffect(isWelding: boolean) {
    // Get current weld machine state
    const { weldMachine } = this.activeEquipment;

    // TODO: fix the return type of activeEquipment.weldMachine to
    // specify that these are weld machine state items (then we don't need to check for 'isWelding' in the state)
    if (weldMachine && 'isWelding' in weldMachine.state) {
      weldMachine.state.isWelding = isWelding;
    }
  }
}
